import { useEffect, useMemo } from "react";

import { clearTaskOptimism, listActiveOptimism } from "helpers/taskOptimism";
import { useGET } from "hooks/useAPI";

export const TASK_NAMES = {
  collectVitals: "collect_vitals",
  completeArrangements: "complete_arrangements",
  scheduleConsultation: "schedule_consultation",
} as const;

export type CaseTask = {
  name: string;
  status: string;
  completed_at: string | null;
};

export type CaseTasksResponse = {
  process_run: { status: string; applied_at: string | null } | null;
  tasks: CaseTask[];
};

export function findTask(
  tasks: CaseTask[],
  name: string,
): CaseTask | undefined {
  return tasks.find((task) => task.name === name);
}

export function isTaskComplete(task: CaseTask | undefined): boolean {
  return task?.status === "completed";
}

const FAST_POLL_WINDOW_MS = 90_000;
const FAST_POLL_INTERVAL_MS = 5_000;
const SLOW_POLL_INTERVAL_MS = 30_000;

/**
 * Polls fast while any active latch is still waiting on its task to show
 * completed in the API response, slows down once the oldest such latch
 * passes 90s, and stops entirely once nothing is pending.
 */
function computeRefreshInterval(
  data: CaseTasksResponse | undefined,
  caseId: string,
): number {
  const tasks = data?.tasks ?? [];
  const pendingAges = listActiveOptimism(caseId)
    .filter(({ taskName }) => !isTaskComplete(findTask(tasks, taskName)))
    .map(({ ageMs }) => ageMs);

  if (pendingAges.length === 0) return 0;

  const oldestAge = Math.max(...pendingAges);
  return oldestAge < FAST_POLL_WINDOW_MS
    ? FAST_POLL_INTERVAL_MS
    : SLOW_POLL_INTERVAL_MS;
}

type UseCaseTasksOptions = {
  enabled: boolean;
};

export function useCaseTasks(
  caseId: string | null | undefined,
  { enabled }: UseCaseTasksOptions,
) {
  const shouldFetch = enabled && !!caseId;
  const { data, isLoading, error } = useGET(
    shouldFetch ? `/cases/${caseId}/tasks` : null,
    undefined,
    {
      shouldRetryOnError: true,
      refreshInterval: (latestData?: CaseTasksResponse) =>
        caseId ? computeRefreshInterval(latestData, caseId) : 0,
    },
  );

  const typedData = data as CaseTasksResponse | undefined;
  const tasks = useMemo(() => typedData?.tasks ?? [], [typedData?.tasks]);

  useEffect(() => {
    if (!caseId) return;
    listActiveOptimism(caseId).forEach(({ taskName }) => {
      if (isTaskComplete(findTask(tasks, taskName))) {
        clearTaskOptimism(caseId, taskName);
      }
    });
  }, [caseId, tasks]);

  return {
    data: typedData,
    tasksAvailable: !!typedData?.process_run,
    tasks,
    isLoading,
    error,
  };
}
